import { ExternalLink } from 'lucide-react'
import type { Column } from '@/components/ui/DataTable'
import { StatusBadge } from '@/components/ui/Badge'
import { Chip } from '@/components/ui/Chip'
import { FavoriteToggle } from './FavoriteToggle'
import { FlagToggle } from './FlagToggle'
import { pathOf, truncateMiddle } from '@/lib/format'
import type { Reviewable } from '@/types'

/**
 * Column factories shared across review features. The favorite, flag, tags,
 * and status columns were copy-pasted four times before this file existed
 * (docs/06-component-refactor.md §4.5).
 *
 * Feature-local `columns.tsx` files compose these with their entity-specific
 * columns and export a `buildColumns(params)`.
 */

export function favoriteColumn<T extends Reviewable>(
  onToggle: (id: string, favorite: boolean) => void,
): Column<T> {
  return {
    key: 'favorite',
    header: '',
    width: '36px',
    render: (row) => (
      <FavoriteToggle favorite={row.favorite} onChange={(next) => onToggle(row.id, next)} />
    ),
  }
}

export function flagColumn<T extends Reviewable>(
  onToggle: (id: string, flagged: boolean) => void,
): Column<T> {
  return {
    key: 'flagged',
    header: '',
    width: '36px',
    render: (row) => (
      <FlagToggle flagged={row.flagged} onChange={(next) => onToggle(row.id, next)} />
    ),
  }
}

/** Chips up to `max`, then a `+N` counter. Never wraps a row to three lines. */
export function tagsColumn<T extends Reviewable>(max = 2, width = '170px'): Column<T> {
  return {
    key: 'tags',
    header: 'Tags',
    width,
    render: (row) => (
      <span className="flex flex-wrap gap-1">
        {row.tags.slice(0, max).map((t) => (
          <Chip key={t} label={t} />
        ))}
        {row.tags.length > max && (
          <span className="text-[11px] text-ink-faint">+{row.tags.length - max}</span>
        )}
      </span>
    ),
  }
}

export function statusColumn<T extends Reviewable>(width = '110px'): Column<T> {
  return {
    key: 'status',
    header: 'Status',
    width,
    sortBy: (row) => row.status,
    render: (row) => <StatusBadge status={row.status} />,
  }
}

/**
 * External source link. `showPath` renders the truncated path alongside the
 * icon; without it the column is icon-only and narrow.
 */
export function sourceLinkColumn<T extends { id: string }>(
  href: (row: T) => string | null,
  opts: { showPath?: boolean; width?: string; header?: string; label?: (row: T) => string } = {},
): Column<T> {
  const { showPath = false, width = showPath ? '190px' : '70px', header = 'Source', label } = opts

  return {
    key: 'source',
    header,
    width,
    sortBy: (row) => href(row) ?? '',
    render: (row) => {
      const url = href(row)
      if (!url) return <span className="text-[12px] text-ink-faint">—</span>
      return (
        <span className="flex items-center gap-1.5">
          {showPath && (
            <span className="font-mono text-[12px] text-ink-muted" title={url}>
              {truncateMiddle(pathOf(url), 22)}
            </span>
          )}
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            aria-label={label ? label(row) : 'Open source page'}
            className="inline-flex text-ink-faint hover:text-brand-blue"
          >
            <ExternalLink size={12} aria-hidden />
          </a>
        </span>
      )
    },
  }
}
